import React, { useState, useEffect, useRef, useMemo } from "react";
import { toastError } from "../../../../utils/showApiErrorToast";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Flex,
  Text,
  Badge,
  Button,
  VStack,
  HStack,
  IconButton,
  useColorModeValue,
  Textarea,
  Input,
  SimpleGrid,
} from "@chakra-ui/react";
import { toast } from "sonner";
import { Signal } from "./SignalCard";
import { CopyIcon, EditIcon, CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { getLongGradient, getShortGradient } from "../../../../utils/signalColors";
import { calculateSignalRisk } from "../../../../services/signalsApi";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";

interface SignalDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  signal: Signal | null;
  assetSymbol: string;
  onPlaceOrder?: (
    signal: Signal,
    overrides?: {
      lotSize?: number;
      stopLossPrice?: number;
      takeProfitPrice?: number;
    }
  ) => void;
}

const SignalDetailsModal: React.FC<SignalDetailsModalProps> = ({
  isOpen,
  onClose,
  signal,
  assetSymbol,
  onPlaceOrder,
}) => {
  const { formatMoney } = useDisplayCurrency();
  const modalBg = useColorModeValue("white", "#151517");
  const borderColor = useColorModeValue("rgba(56, 187, 209, 0.2)", "rgba(56, 187, 209, 0.3)");
  const textColor = useColorModeValue("#575757", "white"); // Glacial Grey
  const labelColor = useColorModeValue("gray.400", "gray.400");
  const progressBgColor = useColorModeValue("#E5E7EB", "#4A5568");
  const textareaBg = useColorModeValue("gray.50", "gray.700");
  const hoverBg = useColorModeValue("rgba(56, 187, 209, 0.1)", "rgba(56, 187, 209, 0.2)");
  const iconHoverBg = useColorModeValue("rgba(56, 187, 209, 0.15)", "rgba(56, 187, 209, 0.25)");
  const sectionBg = useColorModeValue("gray.50", "gray.800");
  const inputBg = useColorModeValue("white", "#1A202C");

  // Body state – does NOT manage timer/expiry; header owns its own timer state
  // Lot size workflow:
  // - User types into lotSizeInput, then clicks Save
  // - Saved value is stored in savedLotSize and used for BE risk calculations
  // - User can later edit savedLotSize via the Lot Size StatRow (also re-triggers BE calls)
  const [lotSizeInput, setLotSizeInput] = useState<string>("");
  const [savedLotSize, setSavedLotSize] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editedValues, setEditedValues] = useState<{
    entry?: string;
    tp?: string;
    sl?: string;
    lotSize?: string;
  }>({});

  // Account balance - set to 0 for now, will be hooked up properly later
  const accountBalance = 0;

  const parsePrice = (s: string | undefined): number => {
    if (s == null || s === "") return 0;
    const n = parseFloat(String(s).replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  const effectiveSl = useMemo(
    () => parsePrice(editedValues.sl || signal?.sl),
    [editedValues.sl, signal?.sl]
  );
  const effectiveTp = useMemo(
    () => parsePrice(editedValues.tp || signal?.tp),
    [editedValues.tp, signal?.tp]
  );
  // Only use savedLotSize for effectiveLotSize so BE calls happen
  // after user explicitly saves (first time or on later edits).
  const effectiveLotSize = useMemo(() => {
    const raw = savedLotSize ?? "";
    const n = parseFloat(String(raw).replace(/,/g, ""));
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [savedLotSize]);

  // Risk/RR and potentials (RR seeded from signal.initialRiskRewardRatio, then refined via calculateSignalRisk)
  const [riskRewardRatio, setRiskRewardRatio] = useState<number | null>(
    signal?.initialRiskRewardRatio ?? null
  );
  const [lossPotential, setLossPotential] = useState<number | null>(null);
  const [profitPotential, setProfitPotential] = useState<number | null>(null);

  const riskCalcTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasOpenRef = useRef(false);

  // Reset lot size and edited values ONLY when the modal transitions
  // from closed -> open. While the modal is open, parent re-renders
  // or prop changes MUST NOT wipe user edits.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current && signal) {
      setLotSizeInput("");
      setSavedLotSize(null);
      setEditingField(null);
      setEditedValues({});
      setRiskRewardRatio(signal.initialRiskRewardRatio ?? null);
      setLossPotential(null);
      setProfitPotential(null);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, signal]);

  // Debounced calculateSignalRisk when lot size, SL, or TP change (minimal debounce for responsive BE-derived RR/Loss/Profit)
  useEffect(() => {
    // Backend now uses event_id as the only identifier; do not fall back to signal.id
    const riskId = signal?.eventId;
    if (!riskId || effectiveLotSize <= 0) {
      setRiskRewardRatio(null);
      setLossPotential(null);
      setProfitPotential(null);
      return;
    }
    if (riskCalcTimeoutRef.current) clearTimeout(riskCalcTimeoutRef.current);
    riskCalcTimeoutRef.current = setTimeout(() => {
      riskCalcTimeoutRef.current = null;
      const body: {
        lot_size: number;
        stop_loss_price?: number;
        take_profit_price?: number;
      } = { lot_size: effectiveLotSize };
      if (effectiveSl > 0) body.stop_loss_price = effectiveSl;
      if (effectiveTp > 0) body.take_profit_price = effectiveTp;
      calculateSignalRisk(riskId, body)
        .then((res) => {
          setRiskRewardRatio(res.risk_reward_ratio);
          setLossPotential(res.risk_amount);
          setProfitPotential(res.profit_amount);
        })
        .catch(() => {
          setRiskRewardRatio(null);
          setLossPotential(null);
          setProfitPotential(null);
        });
    }, 300);
    return () => {
      if (riskCalcTimeoutRef.current) clearTimeout(riskCalcTimeoutRef.current);
    };
  }, [signal?.eventId, effectiveLotSize, effectiveSl, effectiveTp]);

  if (!signal) return null;

  const isLong = signal.type === "LONG";
  const buttonTextGradient = "linear-gradient(135deg, #38BBD1, #3EEAEC)"; // Primary gradient (Tropical Surf to Neon Aqua)
  const buttonStrokeColor = "#38BBD1"; // Tropical Surf

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Copied!", { description: `${label} copied to clipboard` });
    });
  };

  const formatGateName = (gate?: string | null): string | null => {
    if (!gate) return null;
    switch (gate) {
      case "pivot":
        return "Pivot";
      case "pivot_relaxed":
        return "Pivot (relaxed)";
      case "mrd":
        return "MRD";
      case "mrd_relaxed":
        return "MRD (relaxed)";
      case "discount_purchase":
        return "Discount purchase";
      case "velocity_breakout":
        return "Velocity breakout";
      case "both":
        return "Both";
      default:
        return gate.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
    }
  };

  // Simple header – no timer, just type + symbol
  const SignalDetailsHeader: React.FC = () => {
    const badgeBg = signal.type === "LONG" ? getLongGradient() : getShortGradient();
    return (
      // @ts-ignore - Chakra UI responsive props create complex union types
      <Flex align="center" justify="space-between" wrap="wrap" gap={3}>
        <Flex align="center" gap={3} flex={1} minW={0}>
          <Badge
            bg={badgeBg}
            color="white"
            px={4}
            py={2}
            borderRadius="lg"
            fontSize="sm"
            fontWeight="bold"
            background={badgeBg}
            textTransform="uppercase"
            letterSpacing="0.05em"
            fontFamily="'IBM Plex Sans', sans-serif"
            boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
          >
            {signal.type}
          </Badge>
          <Text
            fontSize="xl"
            fontWeight="normal"
            color="#38BBD1"
            fontFamily="'Urbanist', sans-serif"
            noOfLines={1}
          >
            {assetSymbol}
          </Text>
        </Flex>
      </Flex>
    );
  };

  const StatRow: React.FC<{ 
    label: string; 
    value: string; 
    copyValue?: string;
    fieldKey: string;
    onSave?: (newValue: string) => void;
  }> = ({
    label,
    value,
    copyValue,
    fieldKey,
    onSave,
  }) => {
    const valueToCopy = copyValue || value;
    const isEditing = editingField === fieldKey;
    const displayValue = editedValues[fieldKey as keyof typeof editedValues] || value;
    const [editValue, setEditValue] = useState<string>(displayValue);
    const prevIsEditingRef = useRef(isEditing);

    // Only sync editValue from displayValue when entering edit mode (or when not editing).
    // This prevents parent re-renders (e.g. timer, risk calc) from overwriting what the user is typing.
    useEffect(() => {
      if (isEditing && !prevIsEditingRef.current) {
        setEditValue(displayValue);
      } else if (!isEditing) {
        setEditValue(displayValue);
      }
      prevIsEditingRef.current = isEditing;
    }, [isEditing, displayValue]);

    const handleSave = () => {
      if (onSave) {
        onSave(editValue);
      } else {
        setEditedValues(prev => ({ ...prev, [fieldKey]: editValue }));
      }
      setEditingField(null);
      toast.success("Saved!", { description: `${label} updated` });
    };

    const handleCancel = () => {
      setEditValue(displayValue);
      setEditingField(null);
    };

    return (
      // @ts-ignore - Chakra UI Flex component has complex union types
      <Flex
        justify="space-between"
        align={isEditing ? "flex-end" : "center"}
        py={4}
        px={4}
        gap={3}
      >
        <VStack align="start" spacing={1} flex={1}>
          <Text 
            fontSize="xs" 
            color={labelColor} 
            fontWeight="medium"
            textTransform="uppercase"
            letterSpacing="0.05em"
            fontFamily="'IBM Plex Sans', sans-serif"
          >
            {label}
          </Text>
          {isEditing ? (
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              bg={inputBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="md"
              px={4}
              py={2}
              fontSize="xs"
              color={textColor}
              fontWeight="medium"
              fontFamily="'IBM Plex Sans', sans-serif"
              textTransform="uppercase"
              w="100%"
              maxW="200px"
              h="36px"
              _placeholder={{
                color: labelColor,
                fontSize: "xs",
                fontWeight: "medium",
                fontFamily: "'IBM Plex Sans', sans-serif",
                textTransform: "uppercase",
              }}
              _focus={{
                borderColor: "#38BBD1",
                boxShadow: "0 0 0 1px rgba(56, 187, 209, 0.2)",
              }}
              _hover={{
                borderColor: "#38BBD1",
              }}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSave();
                } else if (e.key === "Escape") {
                  handleCancel();
                }
              }}
            />
          ) : (
            <Text 
              fontSize="xl" 
              color={textColor} 
              fontWeight="bold" 
              fontFamily="'Urbanist', sans-serif"
            >
              {displayValue}
            </Text>
          )}
        </VStack>
        {isEditing ? (
          <Button
            onClick={handleSave}
            bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
            color="white"
            fontWeight="medium"
            borderRadius="md"
            px={6}
            py={2}
            fontSize="sm"
            fontFamily="'IBM Plex Sans', sans-serif"
            textTransform="uppercase"
            h="36px"
            alignSelf="flex-end"
            _hover={{
              opacity: 0.9,
            }}
            _active={{
              bgGradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
            }}
          >
            Save
          </Button>
        ) : (
          <HStack spacing={1}>
            <IconButton
              aria-label={`Edit ${label}`}
              icon={<EditIcon />}
              size="sm"
              variant="ghost"
              onClick={() => setEditingField(fieldKey)}
              _hover={{ bg: iconHoverBg }}
              color="#38BBD1"
              transition="background-color 0.15s ease"
            />
            <IconButton
              aria-label={`Copy ${label}`}
              icon={<CopyIcon />}
              size="sm"
              variant="ghost"
              onClick={() => copyToClipboard(valueToCopy, label)}
              _hover={{ bg: iconHoverBg }}
              color="#38BBD1"
              transition="background-color 0.15s ease"
            />
          </HStack>
        )}
      </Flex>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(10px)" bg="rgba(0, 0, 0, 0.1)" />
      <ModalContent bg={modalBg} borderRadius="16px" maxH="90vh" border="1px solid" borderColor={borderColor}>
        <ModalHeader 
          color="#38BBD1" 
          fontFamily="'Urbanist', sans-serif" 
          fontWeight="normal"
          pb={4}
          pt={6}
          pr={12}
          borderBottom="1px solid"
          borderColor={borderColor}
          position="relative"
        >
          <SignalDetailsHeader />
        </ModalHeader>
        <ModalCloseButton 
          top={4}
          right={4}
          color={textColor}
          _hover={{ bg: hoverBg }}
        />
        <ModalBody pb={6} pt={6} overflowY="auto" maxH="calc(90vh - 200px)">
          <VStack align="stretch" spacing={6}>
            {/* Gate context (strategy + dynamic RR), when available */}
            {signal.gateUsed || typeof signal.gateRr === "number" ? (
              <Box
                bg={sectionBg}
                borderRadius="lg"
                p={4}
                border="1px solid"
                borderColor={borderColor}
              >
                <Text
                  fontSize="xs"
                  color={labelColor}
                  fontWeight="medium"
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                  fontFamily="'IBM Plex Sans', sans-serif"
                  mb={2}
                >
                  Gate context
                </Text>
                <VStack align="flex-start" spacing={1}>
                  {signal.gateUsed && (
                    <Text
                      fontSize="sm"
                      color={textColor}
                      fontFamily="'IBM Plex Sans', sans-serif"
                    >
                      Gate used:{" "}
                      <Text as="span" fontWeight="medium">
                        {formatGateName(signal.gateUsed)}
                      </Text>
                    </Text>
                  )}
                  {typeof signal.gateRr === "number" && (
                    <Text
                      fontSize="sm"
                      color={textColor}
                      fontFamily="'IBM Plex Sans', sans-serif"
                    >
                      Gate RR:{" "}
                      <Text as="span" fontWeight="medium">
                        {signal.gateRr.toFixed(2)}x
                      </Text>
                    </Text>
                  )}
                  {!signal.gateUsed && typeof signal.gateRr !== "number" && (
                    <Text
                      fontSize="sm"
                      color={labelColor}
                      fontFamily="'IBM Plex Sans', sans-serif"
                    >
                      No gate context available for this setup.
                    </Text>
                  )}
                </VStack>
              </Box>
            ) : null}

            <Box bg={sectionBg} borderRadius="lg" p={2}>
              <StatRow 
                label="Entry Price" 
                value={editedValues.entry || signal.entry} 
                fieldKey="entry"
              />
              <StatRow 
                label="Take Profit (TP)" 
                value={editedValues.tp || signal.tp} 
                fieldKey="tp"
              />
              <StatRow 
                label="Stop Loss (SL)" 
                value={editedValues.sl || signal.sl} 
                fieldKey="sl"
              />
              
              {/* Lot Size - Show as StatRow once saved, otherwise show input with Save button */}
              {savedLotSize ? (
                <StatRow 
                  label="Lot Size" 
                  value={savedLotSize} 
                  fieldKey="lotSize"
                  onSave={(newValue) => {
                    // Update saved lot size and trigger BE risk recalculation via effectiveLotSize effect
                    setSavedLotSize(newValue);
                  }}
                />
              ) : (
                /* Lot Size Input */
                /* @ts-ignore - Chakra UI Flex component has complex union types */
                <Flex
                  justify="space-between"
                  align="flex-end"
                  py={4}
                  px={4}
                  gap={3}
                >
                  <VStack align="start" spacing={1} flex={1}>
                    <Text 
                      fontSize="xs" 
                      color={labelColor} 
                      fontWeight="medium"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                      fontFamily="'IBM Plex Sans', sans-serif"
                    >
                      Lot Size
                    </Text>
                    <Input
                      value={lotSizeInput}
                      onChange={(e) => setLotSizeInput(e.target.value)}
                      placeholder="Enter lot size"
                      bg={inputBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="md"
                      px={4}
                      py={2}
                      fontSize="xs"
                      color={textColor}
                      fontWeight="medium"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      textTransform="uppercase"
                      w="100%"
                      maxW="200px"
                      h="36px"
                      _placeholder={{
                        color: labelColor,
                        fontSize: "xs",
                        fontWeight: "medium",
                        fontFamily: "'IBM Plex Sans', sans-serif",
                        textTransform: "uppercase",
                      }}
                      _focus={{
                        borderColor: "#38BBD1",
                        boxShadow: "0 0 0 1px rgba(56, 187, 209, 0.2)",
                      }}
                      _hover={{
                        borderColor: "#38BBD1",
                      }}
                      type="number"
                      step="0.01"
                      min="0"
                    />
                  </VStack>
                  <Button
                    onClick={() => {
                      if (lotSizeInput && parseFloat(lotSizeInput) > 0) {
                        setSavedLotSize(lotSizeInput);
                        // Clear the temporary input; effectiveLotSize now comes from savedLotSize
                        setLotSizeInput("");
                        toast.success("Saved!", { description: `Lot size ${lotSizeInput} saved` });
                      }
                    }}
                    bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                    color="white"
                    fontWeight="medium"
                    borderRadius="md"
                    px={6}
                    py={2}
                    fontSize="sm"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    textTransform="uppercase"
                    h="36px"
                    alignSelf="flex-end"
                    _hover={{
                      opacity: 0.9,
                    }}
                    _active={{
                      bgGradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
                    }}
                    isDisabled={!lotSizeInput || parseFloat(lotSizeInput) <= 0}
                  >
                    Save
                  </Button>
                </Flex>
              )}
            </Box>

            {/* RR / Risk / Profit Potential (BE-derived from calculateSignalRisk) */}
            <Box 
                bg={sectionBg}
                borderRadius="lg"
                p={4}
                border="1px solid"
                borderColor={borderColor}
              >
                <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4}>
                  <VStack align="flex-start" spacing={1}>
                    <Text
                      fontSize="xs"
                      color={labelColor}
                      fontWeight="medium"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                      fontFamily="'IBM Plex Sans', sans-serif"
                    >
                      RR
                    </Text>
                    <Text
                      fontSize="lg"
                      color={textColor}
                      fontWeight="bold"
                      fontFamily="'Urbanist', sans-serif"
                    >
                      {riskRewardRatio !== null ? `1:${riskRewardRatio.toFixed(2)}` : "-"}
                    </Text>
                  </VStack>
                  <VStack align="flex-start" spacing={1}>
                    <Text
                      fontSize="xs"
                      color={labelColor}
                      fontWeight="medium"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                      fontFamily="'IBM Plex Sans', sans-serif"
                    >
                      Loss Potential
                    </Text>
                    <Text
                      fontSize="lg"
                      color={lossPotential !== null && lossPotential > 0 ? "red.500" : textColor}
                      fontWeight="bold"
                      fontFamily="'Urbanist', sans-serif"
                    >
                      {lossPotential !== null ? formatMoney(lossPotential) : "-"}
                    </Text>
                  </VStack>
                  <VStack align="flex-start" spacing={1}>
                    <Text
                      fontSize="xs"
                      color={labelColor}
                      fontWeight="medium"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                      fontFamily="'IBM Plex Sans', sans-serif"
                    >
                      Profit Potential
                    </Text>
                    <Text
                      fontSize="lg"
                      color={profitPotential !== null && profitPotential >= 0 ? "green.500" : textColor}
                      fontWeight="bold"
                      fontFamily="'Urbanist', sans-serif"
                    >
                      {profitPotential !== null ? formatMoney(profitPotential) : "-"}
                    </Text>
                  </VStack>
                </SimpleGrid>
              </Box>

            {/* Take Trade Button – expiry is enforced in handler, so body doesn't re-render on timer ticks */}
            <Button
                bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                color="white"
                fontWeight="medium"
                borderRadius="8px"
                px="20px"
                py="10px"
                h="auto"
                w="100%"
                fontFamily="'IBM Plex Sans', sans-serif"
                textTransform="uppercase"
                fontSize="sm"
                display="inline-flex"
                alignItems="center"
                gap="0.5rem"
                transition="all 0.3s ease"
                _hover={{
                  pr: "calc(20px + 0.5rem)",
                }}
                _active={{
                  bgGradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
                }}
                onClick={(e: any) => {
                  if (!signal) return;
                  const now = new Date();
                  const expiry = new Date(signal.expiryTime);
                  if (now >= expiry) {
                    toastError("Trade expired", { description: "This setup has already expired and cannot be taken." });
                    return;
                  }

                  const lotSizeOverride = effectiveLotSize > 0 ? effectiveLotSize : undefined;
                  const slOverride = effectiveSl > 0 ? effectiveSl : undefined;
                  const tpOverride = effectiveTp > 0 ? effectiveTp : undefined;

                  onPlaceOrder?.(signal, {
                    lotSize: lotSizeOverride,
                    stopLossPrice: slOverride,
                    takeProfitPrice: tpOverride,
                  });
                  onClose();
                }}
                sx={{
                  "& .button-arrow": {
                    width: "1.25rem",
                    height: "1.25rem",
                    transition: "transform 0.3s ease",
                    color: "white",
                  },
                  "&:hover .button-arrow": {
                    transform: "translateX(6px)",
                  },
                }}
              >
                Take Trade
                <Box
                  as="svg"
                  className="button-arrow"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  w="1.25rem"
                  h="1.25rem"
                  color="white"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z"
                    clipRule="evenodd"
                  />
                </Box>
              </Button>
              <Text
                fontSize="xs"
                color={labelColor}
                fontFamily="'IBM Plex Sans', sans-serif"
                lineHeight="1.5"
                textAlign="left"
              >
                <Box as="span" color="gray.500">
                  Disclaimer:
                </Box>{" "}
                Once a trade setup is taken, any edits must be made directly in your broker.
              </Text>

            {/* (Signal Reasoning / Confluence section intentionally removed for now) */}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// Decouple the modal from parent re-renders (e.g. Tables / context updates).
// It will only re-render when its own *meaningful* props change:
// - open/closed state
// - which signal is being shown
// - which asset symbol label is shown
//
// NOTE: We intentionally ignore onPlaceOrder function identity so that
// parent re-renders (due to timers / WebSocket updates) don't force the
// modal to re-render and wipe in-progress edits.
export default React.memo(SignalDetailsModal, (prev, next) => {
  const sameOpen = prev.isOpen === next.isOpen;
  const sameSignalId =
    prev.signal?.id === next.signal?.id &&
    prev.signal?.eventId === next.signal?.eventId;
  const sameSymbol = prev.assetSymbol === next.assetSymbol;

  return sameOpen && sameSignalId && sameSymbol;
});

